import { useAuth } from "@/context/AuthContext";
import ApiImage from "@/components/ApiImage";
import WriteButton from "@/components/WriteButton";
import { useEffect, useMemo, useState, useRef, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/pos-api";
import { rupiah, imgUrl, stockLabel } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Search, AlertTriangle, PackagePlus, Upload, Camera, Image as ImageIcon, Loader2, FileDown, Eye, Power, MoreVertical, Link2, History as HistoryIcon, Printer } from "lucide-react";
import MoneyInput from "@/components/MoneyInput";
import { productMatchesSearch } from "@/lib/product-codes.mjs";
import { useSupervisorApproval } from "@/components/SupervisorApprovalDialog";
import ProductPriceHistoryDrawer from "@/components/ProductPriceHistoryDrawer";
import ProductLabelDialog from "@/components/ProductLabelDialog";
import ReceivingPackagesEditor from '@/components/ReceivingPackagesEditor';
import ProductWorkbookPreview from '@/components/ProductWorkbookPreview';
import ProductTierEditor from '@/components/ProductTierEditor';
import { getActiveStore } from '@/lib/scope';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const empty = {
  name: "", barcode: "", category_id: "",
  price_tiers: [{ name: "Eceran", min_qty: 1, price: 0 }],
  units: [{ name: "", pieces: 1, price: 0, barcode: "" }],
  variants: [{ name: "", barcode: "" }],
  cost: 0, stock: 0, min_stock: 5, image: "", note: "",
};

const optimizeProductImage = (file) => new Promise((resolve, reject) => {
  const source = URL.createObjectURL(file);
  const image = new window.Image();
  image.onload = () => {
    const maxSide = 1200;
    const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      URL.revokeObjectURL(source);
      reject(new Error("Foto tidak dapat diproses oleh browser ini"));
      return;
    }
    context.drawImage(image, 0, 0, width, height);
    canvas.toBlob((blob) => {
      URL.revokeObjectURL(source);
      if (!blob) {
        reject(new Error("Foto gagal dikompres"));
        return;
      }
      const extension = blob.type === "image/webp" ? "webp" : blob.type === "image/png" ? "png" : "jpg";
      resolve(new File([blob], `produk-${Date.now()}.${extension}`, { type: blob.type, lastModified: Date.now() }));
    }, "image/webp", 0.82);
  };
  image.onerror = () => {
    URL.revokeObjectURL(source);
    reject(new Error("Format foto tidak dapat dibaca"));
  };
  image.src = source;
});

function ProductUnitPriceSummary({ product, warehouseCatalog }) {
  const tiers = product.price_tiers || [];
  const units = product.units || [];
  const unitNames = new Set(units.map((unit) => String(unit.name || "").trim().toLowerCase()));
  const baseTier = tiers.find((tier) => Number(tier.min_qty || 1) <= 1 && !unitNames.has(String(tier.name || "").trim().toLowerCase()));
  if (!baseTier && !units.length) return <span className="text-[11px] text-ui-muted italic">harga satuan belum diatur</span>;
  const unitChip = (unit, index) => (
    <span key={`u${index}`} data-testid={`price-unit-${product.id}-${index}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-accent-soft border border-accent/30">
      <span className="text-accent-readable font-medium">{unit.name}</span>
      <span className="font-mono text-brand-readable font-semibold">{rupiah(unit.price)}</span>
      {!warehouseCatalog && <><span className="text-ui-muted">·</span><span className="font-mono text-rose-600 font-medium" title={`Harga beli / modal ${unit.name} = cost × ${unit.pieces} pcs`}>beli {rupiah((product.cost || 0) * (unit.pieces || 1))}</span></>}
    </span>
  );
  return <div className="flex flex-wrap gap-1 items-center">
    {baseTier && <span data-testid={`price-base-unit-${product.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-ui-card border border-ui-line">
      <span className="text-ui-text font-medium">{product.base_unit || "Pcs"}</span>
      <span className="font-mono text-brand-readable font-semibold">{rupiah(baseTier.price)}</span>
      {!warehouseCatalog && <><span className="text-ui-muted">·</span><span className="font-mono text-rose-600 font-medium" title="Harga beli / modal per Pcs (otomatis dari Pembelian)">beli {rupiah(product.cost || 0)}</span></>}
    </span>}
    {units.map(unitChip)}
  </div>;
}

export default function Products() {
  const { approveAction, approvalDialog } = useSupervisorApproval();
  const [priceHistoryProduct, setPriceHistoryProduct] = useState(null);
  const [labelProduct, setLabelProduct] = useState(undefined);
  const [labelOpen, setLabelOpen] = useState(false);
  const [priceReason, setPriceReason] = useState("");
  const { readOnly, user } = useAuth();
  const warehouseCatalog = user?.role === "warehouse";
  const searchInputRef = useRef(null);
  const productRows = useRef(new Map());
  const [focusedProduct, setFocusedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [productTab, setProductTab] = useState('data');
  const [packagesDirty, setPackagesDirty] = useState(false);
  const [packagesBusy, setPackagesBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAllBranches, setSavingAllBranches] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [workbookJob, setWorkbookJob] = useState(null);
  const fileRef = useRef(null);
  const updRef = useRef(null);
  const [updatingPrices, setUpdatingPrices] = useState(false);
  const [histFor, setHistFor] = useState(null);
  const [hist, setHist] = useState(null);
  const [priceSettings, setPriceSettings] = useState(null);
  const [excelMenuOpen, setExcelMenuOpen] = useState(false);
  const [pendingCentralProduct, setPendingCentralProduct] = useState(null);
  const [priceControlOpen, setPriceControlOpen] = useState(false);
  const [priceDraftMode, setPriceDraftMode] = useState("central");
  const [priceDraftCategories, setPriceDraftCategories] = useState([]);
  const [savingPriceSettings, setSavingPriceSettings] = useState(false);
  const [savingCentralProduct, setSavingCentralProduct] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
    setFocusedProduct(null);
    searchInputRef.current?.focus();
    searchInputRef.current?.select();
  }, [location.key]);

  const load = async () => {
    try {
      const calls = [api.get("/products"), api.get("/categories")];
      if (user?.role === "admin") calls.push(api.get("/price-settings"));
      const [p, c, ps] = await Promise.all(calls);
      setProducts(p.data);
      setCategories(c.data);
      if (ps) setPriceSettings(ps.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, [user?.role]);

  const updateCentralCategories = async (categories) => {
    try {
      const { data } = await api.put("/price-settings", { mode: "central", central_categories: categories, central_excluded_products: priceSettings?.central_excluded_products || [] });
      setPriceSettings(data);
      toast.success(categories.length ? "Kategori terpilih mengikuti harga pusat" : "Semua kategori mengikuti harga pusat");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const updatePriceMode = async (mode, categories = priceSettings?.central_categories || []) => {
    if (savingPriceSettings) return;
    const message = mode === "central"
      ? `Terapkan harga dari ${priceSettings?.central_source_store_name || "pusat"} untuk kategori yang dipilih?`
      : "Nonaktifkan Harga Terpusat untuk toko ini? Harga dan tingkat harga yang sedang dipakai tetap tersimpan dan dapat diedit melalui Edit Produk.";
    if (!window.confirm(message)) return;
    setSavingPriceSettings(true);
    try {
      const { data } = await api.put("/price-settings", { mode, central_categories: categories, central_excluded_products: priceSettings?.central_excluded_products || [] });
      setPriceSettings(data);
      setPriceControlOpen(false);
      toast.success(mode === "central" ? "Harga Terpusat aktif" : "Harga Terpusat nonaktif. Harga saat ini tetap tersimpan.");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingPriceSettings(false); }
  };

  const toggleCentralProduct = async (product) => {
    if (savingCentralProduct) return;
    setSavingCentralProduct(true);
    try {
      await api.put(`/price-settings/products/${product.id}`);
      const { data } = await api.get("/price-settings");
      setPriceSettings(data);
      setPendingCentralProduct(null);
      load();
      toast.success((data.central_excluded_products || []).includes(product.id) ? "Produk kembali manual" : "Produk mengikuti harga pusat");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingCentralProduct(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/products/template", { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data); link.download = "template_produk.xlsx"; link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openHist = async (p) => {
    setHistFor(p); setHist(null);
    try { const { data } = await api.get(`/products/${p.id}/purchases`); setHist(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openPurchase = (h) => {
    setHistFor(null);
    navigate("/purchases", { state: { openPurchaseId: h.id } });
  };

  const downloadExport = async () => {
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (catFilter !== "all") params.category_id = catFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/products/export", { params, responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data); link.download = "daftar_produk.xlsx"; link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImportResult(null);
    setImporting(true);
    try {
      const storeId = getActiveStore();
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/products/import", fd, { params:{preview:true}, headers: { 'X-Store-Id':storeId } });
      setWorkbookJob({file, mode:'import', storeId, data});
    } catch (err) { setImportResult({ filename: file.name, requestError: apiError(err) }); }
    finally { setImporting(false); }
  };

  const downloadFailedImport = () => {
    try {
      const bytes = Uint8Array.from(atob(importResult.failure_report), character => character.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "produk_perlu_revisi.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("File revisi tidak dapat diunduh. Lihat rincian kesalahan di popup."); }
  };

  const [deletingAll, setDeletingAll] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((prev) => (filtered.length && filtered.every((p) => prev.has(p.id)) ? new Set() : new Set(filtered.map((p) => p.id))));

  const removeSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Hapus ${ids.length} produk terpilih? Tindakan ini tidak bisa dibatalkan.`)) return;
    if (!window.confirm("Yakin? Tekan OK sekali lagi untuk konfirmasi.")) return;
    setDeletingAll(true);
    try {
      const { data } = await api.post("/products/bulk-delete", { ids });
      toast.success(`${data.deleted} produk dihapus`);
      setSelected(new Set()); setSelectMode(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeletingAll(false); }
  };

  const handleUpdatePrices = async (e) => {
    const file = e.target.files?.[0];
    if (updRef.current) updRef.current.value = "";
    if (!file) return;
    setUpdatingPrices(true);
    try {
      const storeId = getActiveStore();
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/products/update-prices", fd, {params:{preview:true}, headers:{'X-Store-Id':storeId}});
      setWorkbookJob({file, mode:'update', storeId, data});
    } catch (err) { toast.error(apiError(err)); }
    finally { setUpdatingPrices(false); }
  };

  const openNew = () => { setEditing(null); setPriceReason(""); setForm(empty); setProductTab('data'); setPackagesDirty(false); setOpen(true); };
const openEdit = (p) => {
    setProductTab('data'); setPackagesDirty(false);
    setEditing(p);
    setPriceReason("");
    setForm({
      name: p.name, barcode: p.barcode || "", category_id: p.category_id || "",
      price_tiers: (p.price_tiers || []).map((t) => ({ name: t.name, min_qty: t.min_qty ?? 1, price: t.price ?? 0 })),
      units: (p.units || []).map((u) => ({ name: u.name, pieces: u.pieces ?? 1, price: u.price ?? 0, barcode: u.barcode || "" })),
      variants: (p.variants || []).map((v) => ({ name: v.name, barcode: v.barcode || "" })),
      cost: p.cost || 0, stock: p.stock || 0, min_stock: p.min_stock || 5, image: p.image || "", note: p.note || "", active: p.active !== false,
    });
    setOpen(true);
  };

  const toggleActive = async (product) => {
    try {
      await api.put(`/products/${product.id}`, { active: product.active === false });
      toast.success(product.active === false ? "Produk diaktifkan dan tampil di kasir" : "Produk dinonaktifkan dari kasir");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async (applyAllBranches = false) => {
    if (!form.name.trim()) return toast.error("Nama produk wajib diisi");
    if (!editing && !form.category_id) return toast.error("Kategori wajib dipilih untuk produk baru");
    if (form.units.some((u) => (u.barcode || "").trim() && (!u.name.trim() || !(Number(u.pieces) > 0)))) {
      return toast.error("Isi nama satuan dan jumlah isi lebih dari 0 untuk SKU satuan");
    }
    if (form.variants.some((v) => (v.barcode || "").trim() && !v.name.trim())) {
      return toast.error("Isi nama varian untuk SKU varian");
    }
    const tiers = form.price_tiers.filter((t) => t.name.trim());
    let approval = {};
    if (applyAllBranches) {
      try { approval = await approveAction("all_branch_price", `${form.name} · terapkan harga dan modal ke semua cabang`); }
      catch (error) { return toast.error(apiError(error)); }
      if (approval === false) return;
    }
    setSaving(true);
    setSavingAllBranches(applyAllBranches);
    const payload = {
      ...form,
      category_id: form.category_id || null,
      note: (form.note || "").trim() || null,
      price_tiers: tiers.map((t) => ({
        name: t.name.trim(),
        min_qty: Number(t.min_qty || 1),
        price: Number(t.price || 0),
      })),
      units: form.units.filter((u) => u.name.trim() && Number(u.pieces) > 0).map((u) => ({
        name: u.name.trim(),
        pieces: Number(u.pieces || 1),
        price: Number(u.price || 0),
        barcode: (u.barcode || "").trim() || null,
      })),
      variants: form.variants.filter((v) => v.name.trim()).map((v) => ({
        name: v.name.trim(),
        barcode: (v.barcode || "").trim() || null,
      })),
      base_unit: editing?.base_unit || "Pcs",
      cost: Number(form.cost || 0),
      stock: Number(form.stock || 0),
      min_stock: Number(form.min_stock || 0),
      ...(editing ? { price_change_reason: priceReason.trim() || null } : {}),
    };
    if (editing) delete payload.stock;
    try {
      let response;
      if (editing) response = await api.put(`/products/${editing.id}`, payload, { ...approval, params: applyAllBranches ? { apply_all_stores: true } : {} });
      else response = await api.post("/products", payload);
      toast.success(applyAllBranches
        ? `${form.name} diterapkan ke ${response.data.applied_store_count || 0} cabang. Keranjang aktif akan memakai harga terbaru.`
        : editing ? "Produk diperbarui" : "Produk ditambahkan");
      if (packagesDirty) {
        setEditing(previous => ({ ...previous, name: response.data.name }));
        toast.info('Data produk tersimpan. Perubahan kemasan belum disimpan; buka tab Kemasan Penerimaan.');
      }
      else setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
      setSavingAllBranches(false);
    }
  };

  const packagesSaved = (id, packages) => {
    setEditing(previous => previous?.id === id ? { ...previous, receiving_packages: packages } : previous);
    setProducts(previous => previous.map(product => product.id === id ? { ...product, receiving_packages: packages } : product));
  };

  const remove = async (p) => {
    if (!window.confirm(`Hapus produk "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Produk dihapus");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "-";
  const followsCentral = (p) => {
    if (!priceSettings || priceSettings.mode !== "central") return false;
    if ((priceSettings.central_excluded_products || []).includes(p.id)) return false;
    const cats = priceSettings.central_categories || [];
    return !cats.length || cats.includes(p.category_id);
  };
  const filtered = useMemo(() => products.filter((p) => {
    if (!productMatchesSearch(p, search)) return false;
    if (catFilter !== "all") {
      if (catFilter === "none" ? !!p.category_id : p.category_id !== catFilter) return false;
    }
    if (statusFilter !== "all") {
      const stock = p.stock || 0, mn = p.min_stock || 0;
      const noPrice = !(p.price_tiers || []).some((t) => Number(t.price) > 0);
      if (statusFilter === "central" && !followsCentral(p)) return false;
      if (statusFilter === "manual" && followsCentral(p)) return false;
      if (statusFilter === "no_price" && !noPrice) return false;
      if (statusFilter === "habis" && stock > 0) return false;
      if (statusFilter === "minus" && stock >= 0) return false;
      if (statusFilter === "low" && !(stock > 0 && stock <= mn)) return false;
    }
    return true;
  }), [products, search, catFilter, statusFilter, priceSettings]);
  const focusProduct = (index) => {
    const product = filtered[index];
    if (!product) return;
    setFocusedProduct(product.id);
    const row = productRows.current.get(product.id);
    row?.focus();
    row?.scrollIntoView({ block: "nearest" });
  };
  const onProductKey = (event, product) => {
    if (event.target !== event.currentTarget) return;
    const index = filtered.findIndex((p) => p.id === product.id);
    if (event.key === "ArrowDown") { event.preventDefault(); focusProduct(Math.min(filtered.length - 1, index + 1)); }
    else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (index === 0) { setFocusedProduct(null); searchInputRef.current?.focus(); } else focusProduct(index - 1);
    } else if (event.key === "Enter" && !readOnly) { event.preventDefault(); openEdit(product); }
  };
  const filterActive = catFilter !== "all" || statusFilter !== "all" || !!search.trim();

  const setTier = (i, key, val) =>
    setForm((f) => ({ ...f, price_tiers: f.price_tiers.map((t, x) => (x === i ? { ...t, [key]: val } : t)) }));
  const addTier = () =>
    setForm((f) => ({ ...f, price_tiers: [...f.price_tiers, { name: "", min_qty: 1, price: 0 }] }));
  const removeTier = (i) =>
    setForm((f) => ({ ...f, price_tiers: f.price_tiers.filter((_, x) => x !== i) }));

  const setUnit = (i, key, val) =>
    setForm((f) => ({ ...f, units: f.units.map((u, x) => (x === i ? { ...u, [key]: val } : u)) }));
  const addUnit = () =>
    setForm((f) => ({ ...f, units: [...f.units, { name: "", pieces: 1, price: 0, barcode: "" }] }));
  const removeUnit = (i) =>
    setForm((f) => ({ ...f, units: f.units.filter((_, x) => x !== i) }));

  const setVariant = (i, key, val) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v, x) => (x === i ? { ...v, [key]: val } : v)) }));
  const addVariant = () =>
    setForm((f) => ({ ...f, variants: [...f.variants, { name: "", barcode: "" }] }));
  const removeVariant = (i) =>
    setForm((f) => ({ ...f, variants: f.variants.filter((_, x) => x !== i) }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const optimized = await optimizeProductImage(file);
      const fd = new FormData();
      fd.append("file", optimized);
      // Let the browser add the multipart boundary. Setting Content-Type by hand
      // can omit the boundary on mobile Safari, so FastAPI sees no `file` field.
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Foto dikompres dan terunggah");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <>
      {approvalDialog}
      <ProductPriceHistoryDrawer product={priceHistoryProduct} onClose={() => setPriceHistoryProduct(null)} onRestored={load} />
      <ProductLabelDialog open={labelOpen} onClose={() => setLabelOpen(false)} products={products} initialProduct={labelProduct} />
      <PageHeader title="Produk & Stok" subtitle={`${products.length} produk terdaftar`}>
        <div className="flex items-center gap-2">
          {!warehouseCatalog && <Button data-testid="product-labels-btn" variant="outline" onClick={() => { setLabelProduct(undefined); setLabelOpen(true); }} className="gap-2"><Printer className="h-4 w-4" /><span className="hidden sm:inline">Cetak Label</span></Button>}
          <input ref={fileRef} data-testid="product-import-file" type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
          <input ref={updRef} data-testid="product-updateprice-file" type="file" accept=".xlsx,.xls" onChange={handleUpdatePrices} className="hidden" />
          {!warehouseCatalog && <WriteButton data-testid="product-select-btn" variant="outline" onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }} disabled={!products.length} className={`gap-2 bg-transparent ${selectMode ? "border-ui-line text-ui-text hover:bg-ui-card" : "border-danger/50 text-danger-readable hover:bg-danger-soft"}`}>
            <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">{selectMode ? "Batal Pilih" : "Hapus Produk"}</span>
          </WriteButton>}
          <WriteButton data-testid="add-product-btn" onClick={openNew} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Tambah Produk</span>
          </WriteButton>
          {priceSettings && !warehouseCatalog && <button type="button" data-testid="store-price-settings" aria-haspopup="dialog" title={`Harga Terpusat ${priceSettings.mode === "central" ? "aktif" : "nonaktif"}`} onClick={() => { setPriceDraftMode(priceSettings.mode); setPriceDraftCategories(priceSettings.central_categories || []); setPriceControlOpen(true); }} className={`rounded-lg border px-2.5 py-2 text-left hover:bg-ui-soft ${priceSettings.mode === "central" ? "border-success/50 bg-success-soft" : "border-ui-line bg-ui-card"}`}><span className={`text-xs font-semibold ${priceSettings.mode === "central" ? "text-success-readable" : "text-ui-muted"}`}>Harga Terpusat</span></button>}
          <div className="relative">
            <Button type="button" data-testid="product-tools-menu" variant="outline" aria-label="Menu alat produk" aria-expanded={excelMenuOpen} onClick={() => setExcelMenuOpen((v) => !v)} className="h-10 w-10 p-0 border-ui-line bg-transparent hover:bg-ui-card">
              <MoreVertical className="h-5 w-5" />
            </Button>
            {excelMenuOpen && <div data-testid="product-tools-dropdown" className="absolute right-0 top-12 z-30 w-56 rounded-xl border border-ui-line bg-ui-card p-1.5 shadow-xl">
              <button type="button" data-testid="product-template-btn" onClick={() => { setExcelMenuOpen(false); downloadTemplate(); }} className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-ui-soft"><FileDown className="h-4 w-4 text-ui-muted" />Download Template</button>
              <button type="button" data-testid="product-import-btn" onClick={() => { setExcelMenuOpen(false); fileRef.current?.click(); }} disabled={importing} className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-ui-soft disabled:opacity-50">{importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-brand-readable" />}Import Produk</button>
              {!warehouseCatalog && <button type="button" data-testid="product-updateprice-btn" onClick={() => { setExcelMenuOpen(false); updRef.current?.click(); }} disabled={updatingPrices} className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-ui-soft disabled:opacity-50">{updatingPrices ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 text-warning-readable" />}Update Harga</button>}
              {!warehouseCatalog && <button type="button" data-testid="product-export-btn" onClick={() => { setExcelMenuOpen(false); downloadExport(); }} className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-ui-soft"><FileDown className="h-4 w-4 rotate-180 text-ui-muted" />Export Produk</button>}
            </div>}
          </div>
        </div>
      </PageHeader>

      <Dialog open={priceControlOpen} onOpenChange={(value) => { if (!savingPriceSettings) setPriceControlOpen(value); }}>
        <DialogContent data-testid="price-control-dialog" className="bg-ui-card border-ui-line text-ui-text max-w-lg">
          <DialogHeader><DialogTitle>Pengaturan Harga Terpusat</DialogTitle><DialogDescription className="text-ui-muted">Harga pusat bersumber dari <b>{priceSettings?.central_source_store_name || "Sederhana Makmur 3"}</b>. Atur status untuk {priceSettings?.store_name || "toko ini"}, lalu konfirmasi perubahan.</DialogDescription></DialogHeader>
          <fieldset disabled={savingPriceSettings || readOnly} className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-lg border border-ui-line p-3">
              <div className="space-y-1">
                <Label htmlFor="central-price-enabled">Harga Terpusat</Label>
                <p data-testid="central-price-status" className={`text-xs ${priceDraftMode === "central" ? "text-success-readable" : "text-ui-muted"}`}>{priceDraftMode === "central" ? "Aktif" : "Nonaktif"}</p>
              </div>
              <Switch id="central-price-enabled" data-testid="central-price-toggle" aria-label="Aktifkan Harga Terpusat" checked={priceDraftMode === "central"} onCheckedChange={(checked) => setPriceDraftMode(checked ? "central" : "store")} disabled={savingPriceSettings || readOnly} className="data-[state=checked]:bg-success data-[state=unchecked]:bg-ui-muted-surface" />
            </div>
            {priceDraftMode === "store" && <p className="text-sm text-ui-muted">Harga dan tingkat harga saat ini tetap tersimpan. Setelah dinonaktifkan, ubah harga melalui Edit Produk.</p>}
            {priceDraftMode === "central" && <div className="space-y-2"><div className="text-xs text-ui-muted">Kategori yang mengikuti pusat</div><button type="button" onClick={() => setPriceDraftCategories([])} className={`rounded-md border px-2 py-1 text-xs ${!priceDraftCategories.length ? "border-brand text-brand-readable" : "border-ui-line text-ui-muted"}`}>Semua Kategori</button><div className="flex flex-wrap gap-1">{categories.map((c) => { const selected = priceDraftCategories.includes(c.id); return <button type="button" key={c.id} onClick={() => setPriceDraftCategories(selected ? priceDraftCategories.filter((id) => id !== c.id) : [...priceDraftCategories, c.id])} className={`rounded-md border px-2 py-1 text-xs ${selected ? "border-brand bg-brand-soft text-brand-readable" : "border-ui-line text-ui-muted"}`}>{c.name}</button>; })}</div></div>}
          </fieldset>
          <DialogFooter><Button variant="outline" disabled={savingPriceSettings} onClick={() => setPriceControlOpen(false)} className="border-ui-line bg-transparent">Batal</Button><WriteButton data-testid="save-price-settings" disabled={savingPriceSettings} onClick={() => updatePriceMode(priceDraftMode, priceDraftCategories)} className="bg-brand-button text-ui-text font-bold">{savingPriceSettings ? "Menyimpan…" : "Konfirmasi Perubahan"}</WriteButton></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
            <Input
              ref={searchInputRef}
              data-testid="product-search"
              onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); focusProduct(0); } }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari produk / SKU / barcode…"
              className="pl-10 bg-ui-card border-ui-line"
            />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="product-cat-filter" className="w-[170px] bg-ui-card border-ui-line">
              <SelectValue placeholder="Kategori" />
            </SelectTrigger>
            <SelectContent className="bg-ui-card border-ui-line text-ui-text">
              <SelectItem value="all">Semua Kategori</SelectItem>
              <SelectItem value="none">Tanpa Kategori</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="product-status-filter" className="w-[180px] bg-ui-card border-ui-line">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-ui-card border-ui-line text-ui-text">
              <SelectItem value="all">Semua Status</SelectItem>
              {!warehouseCatalog && <SelectItem value="central">Ikut Harga Pusat</SelectItem>}
              {!warehouseCatalog && <SelectItem value="manual">Harga Manual</SelectItem>}
              <SelectItem value="no_price">Belum Ada Harga</SelectItem>
              <SelectItem value="minus">Stok Minus</SelectItem>
              <SelectItem value="habis">Stok Habis (≤0)</SelectItem>
              <SelectItem value="low">Stok Menipis</SelectItem>
            </SelectContent>
          </Select>
          <span data-testid="product-filter-count" className="text-xs text-ui-muted">{filtered.length} produk</span>
          {filterActive && (
            <Button data-testid="product-filter-reset" variant="ghost" size="sm" onClick={() => { setSearch(""); setCatFilter("all"); setStatusFilter("all"); }} className="h-8 text-ui-muted hover:text-ui-text gap-1">
              <span className="text-xs">Reset filter</span>
            </Button>
          )}
        </div>

        <div className="rounded-xl border border-ui-line overflow-hidden">
          {selectMode && (
            <div className="flex flex-wrap items-center gap-3 border-b border-danger/40 bg-danger-soft p-3" data-testid="select-toolbar">
              <label className="flex items-center gap-2 text-sm text-ui-text cursor-pointer">
                <input type="checkbox" data-testid="select-all-checkbox" checked={filtered.length > 0 && filtered.every((p) => selected.has(p.id))} onChange={toggleAll} className="h-4 w-4 accent-brand" />
                Pilih Semua ({filtered.length})
              </label>
              <span className="text-sm text-ui-muted">Terpilih: <b className="text-ui-text">{selected.size}</b></span>
              <WriteButton data-testid="delete-selected-btn" onClick={removeSelected} disabled={!selected.size || deletingAll} variant="outline" className="ml-auto border-danger/50 bg-transparent text-danger-readable hover:bg-danger-soft gap-2">
                {deletingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Hapus Terpilih ({selected.size})
              </WriteButton>
            </div>
          )}
          <table className="w-full text-sm" data-testid="product-management-table">
            <thead className="bg-ui-card text-ui-muted text-xs uppercase tracking-wide">
              <tr>
                {selectMode && <th className="w-10 p-3"></th>}
                <th className="text-left p-3">Produk</th>
                <th className="text-left p-3 hidden md:table-cell">Kategori</th>
                <th className="text-left p-3">{warehouseCatalog ? "Satuan" : "Harga Jual & Beli"}</th>
                <th className="text-center p-3">Stok</th>
                <th className="text-right p-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`} ref={(element) => { if (element) productRows.current.set(p.id, element); else productRows.current.delete(p.id); }}
                  tabIndex={focusedProduct === p.id ? 0 : -1} onFocus={() => setFocusedProduct(p.id)} onKeyDown={(event) => onProductKey(event, p)}
                  className={`border-t border-ui-line hover:bg-ui-soft focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand ${selectMode && selected.has(p.id) ? "bg-brand-soft" : ""}`}>
                  {selectMode && (
                    <td className="p-3 align-top">
                      <input type="checkbox" data-testid={`select-product-${p.id}`} checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} className="h-4 w-4 accent-brand" />
                    </td>
                  )}
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {p.image && <ApiImage src={imgUrl(p.image)} alt="" className="h-8 w-8 rounded object-cover border border-ui-line" />}
                      <div>
                        <div data-testid={`product-name-${p.id}`} onDoubleClick={() => { if (!readOnly && !warehouseCatalog) openEdit(p); }} title="Klik dua kali untuk edit produk" className="font-medium cursor-pointer">{p.name} {p.active === false && <span className="ml-1 text-[10px] rounded bg-danger-soft text-danger-readable px-1.5 py-0.5 align-middle">NONAKTIF</span>}</div>
                        <div className="flex items-center gap-1.5">
                          {p.barcode && <span className="text-[11px] text-ui-muted font-mono">{p.barcode}</span>}
                          {!warehouseCatalog && <button data-testid={`peek-purchase-${p.id}`} onClick={() => openHist(p)} title="Intip histori pembelian" className="text-ui-muted hover:text-brand-readable">
                            <Eye className="h-3.5 w-3.5" />
                          </button>}
                        </div>
                        {!warehouseCatalog && p.last_supplier_name && (
                          <div data-testid={`last-supplier-${p.id}`} className="text-[11px] text-warning-readable/90" title="Supplier & tanggal pembelian terakhir">
                            ↳ beli terakhir: {p.last_supplier_name}{p.last_purchase_at ? ` · ${new Date(p.last_purchase_at).toLocaleDateString("id-ID")}` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell text-ui-muted">{catName(p.category_id)}</td>
                  <td className="p-3">
                    <ProductUnitPriceSummary product={p} warehouseCatalog={warehouseCatalog} />
                  </td>
                  <td className="p-3 text-center">
                    <span className={`inline-flex items-center gap-1 ${p.stock <= p.min_stock ? "text-warning-readable" : "text-ui-muted"}`}>
                      {p.stock <= p.min_stock && <AlertTriangle className="h-3.5 w-3.5" />}
                      <span className="text-xs">{stockLabel(p.stock, p.units, p.base_unit)}</span>
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {!warehouseCatalog && priceSettings && priceSettings.store_id !== priceSettings.central_source_store_id && <button type="button" data-testid={`central-price-badge-${p.id}`} onClick={() => setPendingCentralProduct(p)} disabled={readOnly || priceSettings.mode !== "central" || (!!priceSettings.central_categories?.length && !priceSettings.central_categories.includes(p.category_id))} title={priceSettings.mode !== "central" ? "Harga Terpusat nonaktif" : followsCentral(p) ? "Nonaktifkan harga pusat untuk produk ini" : "Atur keikutsertaan produk dalam harga pusat"} className={`h-8 w-8 rounded-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${followsCentral(p) ? "bg-warning-soft text-warning-readable" : "bg-ui-card text-ui-muted"}`}><Link2 className="h-4 w-4" /></button>}
                      {!warehouseCatalog && <WriteButton plain data-testid={`toggle-active-${p.id}`} onClick={() => toggleActive(p)} title={p.active === false ? "Aktifkan dan tampilkan di kasir" : "Nonaktifkan dari kasir"} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-ui-soft flex items-center justify-center">
                        <Power className={`h-4 w-4 ${p.active === false ? "text-danger-readable" : "text-brand-readable"}`} />
                      </WriteButton>}
                      {!warehouseCatalog && <button type="button" data-testid={`price-history-${p.id}`} onClick={() => setPriceHistoryProduct(p)} title="Riwayat harga" aria-label={`Riwayat harga ${p.name}`} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-brand-soft flex items-center justify-center"><HistoryIcon className="h-4 w-4 text-brand-readable" /></button>}
                      {!warehouseCatalog && <button type="button" data-testid={`print-label-${p.id}`} onClick={() => { setLabelProduct(p); setLabelOpen(true); }} title="Cetak barcode dan label harga" aria-label={`Cetak label ${p.name}`} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-brand-soft flex items-center justify-center"><Printer className="h-4 w-4 text-brand-readable" /></button>}
                      {!warehouseCatalog && <WriteButton plain data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-ui-muted-surface flex items-center justify-center">
                        <Pencil className="h-4 w-4 text-ui-muted" />
                      </WriteButton>}
                      {!warehouseCatalog && <WriteButton plain data-testid={`delete-product-${p.id}`} onClick={() => remove(p)} className="h-8 w-8 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center">
                        <Trash2 className="h-4 w-4 text-danger-readable" />
                      </WriteButton>}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={selectMode ? 6 : 5} className="p-10 text-center text-ui-muted">
                  <PackagePlus className="h-8 w-8 mx-auto mb-2" /> Belum ada produk
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!pendingCentralProduct} onOpenChange={(v) => { if (!v && !savingCentralProduct) setPendingCentralProduct(null); }}>
        <DialogContent data-testid="central-sync-confirm" className="bg-ui-card border-ui-line text-ui-text max-w-md">
          <DialogHeader>
            <DialogTitle>Konfirmasi Perubahan Harga</DialogTitle>
            <DialogDescription className="text-ui-muted text-sm">
              {pendingCentralProduct && ((priceSettings?.central_excluded_products || []).includes(pendingCentralProduct.id)
                ? `Produk ${pendingCentralProduct.name} akan mengikuti harga pusat SM 3, termasuk semua tingkat harga dan satuannya.`
                : `Nonaktifkan harga pusat untuk ${pendingCentralProduct.name}? Harga dan tingkat harga saat ini tetap tersimpan untuk diedit melalui Edit Produk.`)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={savingCentralProduct} onClick={() => setPendingCentralProduct(null)} className="border-ui-line bg-transparent">Batal</Button>
            <WriteButton data-testid="confirm-central-sync" disabled={savingCentralProduct} onClick={() => toggleCentralProduct(pendingCentralProduct)} className="bg-brand-button text-ui-text font-bold">{savingCentralProduct ? "Menyimpan…" : (priceSettings?.central_excluded_products || []).includes(pendingCentralProduct?.id) ? "Aktifkan" : "Nonaktifkan"}</WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {workbookJob && <ProductWorkbookPreview key={`${workbookJob.storeId}:${workbookJob.mode}:${workbookJob.file.name}`} job={workbookJob} onClose={()=>setWorkbookJob(null)} onSaved={result=>{setWorkbookJob(null);setImportResult(result);load();}}/>}
      <Dialog open={!!importResult} onOpenChange={(value) => !value && setImportResult(null)}>
        <DialogContent data-testid="product-import-result" className="bg-ui-card border-ui-line text-ui-text w-[calc(100%-2rem)] max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0 min-w-0">
            <DialogTitle>{importResult?.requestError ? "Impor tidak selesai" : "Hasil Impor Produk"}</DialogTitle>
            <DialogDescription className="text-ui-muted break-all">{importResult?.filename}</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 overflow-y-auto space-y-4 text-sm pr-1" data-testid="product-import-details">
            {importResult?.requestError ? (
              <div role="alert" className="rounded-lg border border-danger/30 bg-danger-soft p-3 text-danger-readable">
                <p>{importResult.requestError}</p>
                <p className="mt-2">Jika koneksi terputus, periksa daftar produk sebelum mencoba impor ulang.</p>
              </div>
            ) : (
              <>
                <p className="text-ui-muted">{importResult?.created || 0} produk baru · {importResult?.updated || 0} produk diperbarui · {importResult?.tier_changes?.length || 0} aksi tingkat harga. {(importResult?.results || []).filter(item => item.status === "failed").length} produk/baris tidak berhasil atau dilewati.</p>
                {[{ status: "failed", title: "Tidak berhasil / perlu revisi" }, { status: "success", title: "Berhasil diimpor" }].map(section => {
                  const items = (importResult?.results || []).filter(item => item.status === section.status);
                  return <section key={section.status} data-testid={`product-import-${section.status}`} className="space-y-2">
                    <h3 className={`font-semibold ${section.status === "failed" ? "text-danger-readable" : "text-brand-readable"}`}>{section.title} ({items.length})</h3>
                    {items.length ? <ul className="space-y-2">
                      {items.map((item, index) => <li key={index} className="rounded-lg border border-ui-line p-3 break-words">
                        <p className="font-medium">{item.name || "Nama produk kosong"}</p>
                        <p className="text-xs text-ui-muted">Baris Excel: {item.rows.join(", ")}</p>
                        {item.reasons?.length > 0 && <ul className="mt-2 list-disc pl-4 space-y-1 text-danger-readable">{item.reasons.map((reason, reasonIndex) => <li key={reasonIndex}>{reason}</li>)}</ul>}
                      </li>)}
                    </ul> : <p className="text-ui-muted">{section.status === "failed" ? "Tidak ada produk yang gagal." : "Tidak ada produk yang berhasil diimpor."}</p>}
                  </section>;
                })}
                {importResult?.failure_report && <p className="text-ui-muted">Unduh file revisi, perbaiki data sesuai kolom alasan_gagal, lalu impor ulang. Produk yang sudah ada dapat diperbaiki melalui Edit Produk; jangan diimpor ulang sebagai produk baru.</p>}
                {importResult?.results?.length === 0 && !importResult?.tier_changes?.length && <p className="text-ui-muted">File tidak berisi baris produk atau tingkat harga.</p>}
              </>
            )}
          </div>
          <DialogFooter className="shrink-0 gap-2">
            {importResult?.failure_report && <Button type="button" variant="outline" data-testid="product-import-download-failed" onClick={downloadFailedImport} className="gap-2"><FileDown className="h-4 w-4" />Download Produk Gagal</Button>}
            <Button type="button" data-testid="product-import-result-close" onClick={() => setImportResult(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!histFor} onOpenChange={(v) => !v && setHistFor(null)}>
        <DialogContent data-testid="peek-purchase-dialog" className="bg-ui-card border-ui-line text-ui-text max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Histori Pembelian · {histFor?.name}</DialogTitle>
            <DialogDescription className="text-ui-muted text-xs">Klik No. pembelian untuk membuka detailnya.</DialogDescription>
          </DialogHeader>
          {hist === null ? (
            <div className="py-8 text-center text-ui-muted text-sm">Memuat…</div>
          ) : hist.length === 0 ? (
            <div className="py-8 text-center text-ui-muted text-sm">Belum ada histori pembelian untuk produk ini</div>
          ) : (
            <>
              <div data-testid="peek-summary" className="flex items-center justify-between text-xs bg-ui-card border border-ui-line rounded-lg px-3 py-2">
                <span className="text-ui-muted">{hist.length}x pembelian</span>
                <span className="text-ui-muted font-mono">Total {hist.reduce((s, h) => s + Number(h.qty || 0) * Number(h.pieces || 1), 0)} pcs · {rupiah(hist.reduce((s, h) => s + Number(h.total ?? (Number(h.qty || 0) * Number(h.cost || 0))), 0))}</span>
              </div>
              <div className="max-h-[50vh] overflow-y-auto space-y-1.5 mt-2" data-testid="peek-purchase-list">
                {hist.map((h, i) => {
                  const pcs = Number(h.pieces || 1);
                  const totalPcs = Number(h.qty || 0) * pcs;
                  const total = Number(h.total ?? (Number(h.qty || 0) * Number(h.cost || 0)));
                  const perPcs = h.calculation?.base_unit_cost != null ? Number(h.calculation.base_unit_cost) : totalPcs > 0 ? total / totalPcs : 0;
                  return (
                  <div key={i} data-testid={`peek-purchase-item-${i}`} className="rounded-lg border border-ui-line bg-ui-soft px-3 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <button data-testid={`peek-invoice-${i}`} onClick={() => openPurchase(h)} className="text-sm font-semibold text-brand-readable hover:underline font-mono">{h.invoice_no}</button>
                      <div className="text-[11px] text-ui-muted">{h.supplier_name || "-"} · {h.date || h.created_at}</div>
                    </div>
                    <div className="text-right shrink-0 font-mono">
                      <div className="text-sm text-ui-text">{h.qty} {h.unit}{pcs > 1 ? ` = ${totalPcs} ${h.base_unit || "Pcs"}` : ""}</div>
                      <div className="text-[11px] text-brand-readable">total {rupiah(total)}</div>
                      <div data-testid={`peek-perpcs-${i}`} className="text-[11px] text-warning-readable">{rupiah(perPcs)} / {h.base_unit || "Pcs"}</div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={value => { if (!saving && !packagesBusy) setOpen(value); }}>
        <DialogContent data-testid="product-form-dialog"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            setFocusedProduct(null);
            searchInputRef.current?.focus();
            searchInputRef.current?.select();
          }}
          className="bg-ui-card border-ui-line text-ui-text w-[calc(100%-2rem)] max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Produk" : "Tambah Produk"}</DialogTitle>
            <DialogDescription className="text-ui-muted text-xs">Atur detail produk, stok, dan tingkat harga per minimal qty.</DialogDescription>
          </DialogHeader>
          <Tabs value={productTab} onValueChange={setProductTab} className="min-w-0">
            <TabsList aria-label="Bagian edit produk" className="sticky -top-6 z-10 w-full sm:w-auto bg-ui-soft">
              <TabsTrigger value="data" data-testid="product-tab-data" disabled={saving || packagesBusy}>Data Produk</TabsTrigger>
              {!warehouseCatalog && <TabsTrigger value="packages" data-testid="product-tab-packages" disabled={saving || packagesBusy}>Kemasan Penerimaan{packagesDirty ? ' •' : ''}</TabsTrigger>}
            </TabsList>
          <TabsContent value="data" forceMount hidden={productTab !== 'data'} className="min-w-0 space-y-4">
          <div className="min-w-0 space-y-4">
            <Field label="Nama Produk">
              <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-ui-card border-ui-line" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="SKU / Barcode Produk">
                <Input data-testid="form-barcode" aria-label="SKU / Barcode Produk" placeholder="Ketik atau scan SKU produk" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="bg-ui-card border-ui-line font-mono" />
              </Field>
              <Field label={editing ? "Kategori" : "Kategori *"}>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="form-category" className="bg-ui-card border-ui-line"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                  <SelectContent className="bg-ui-card border-ui-line text-ui-text">
                    {editing && <SelectItem value="none">Tanpa Kategori</SelectItem>}
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="rounded-xl border border-ui-line p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-ui-muted font-semibold">Satuan Produk (mis. BKS / SLP / Dus)</div>
                <WriteButton type="button" data-testid="add-unit-btn" onClick={addUnit} size="sm" variant="outline" className="h-8 border-ui-line bg-transparent hover:bg-ui-card gap-1 text-xs">
                  <Plus className="h-3 w-3" /> Satuan
                </WriteButton>
              </div>
              {form.units.length === 0 ? (
                <p className="text-[11px] text-ui-muted">Stok disimpan dalam Pcs. Tambahkan satuan seperti Dus (mis. isi 24) — menjual 1 Dus otomatis memotong 24 Pcs.</p>
              ) : (
                <div className="overflow-x-auto">
                <div className="grid min-w-[680px] grid-cols-[1fr_1.4fr_64px_1fr_1fr_36px] gap-2 items-center">
                  <Label className="text-[10px] text-ui-muted uppercase">Nama</Label>
                  <Label className="text-[10px] text-ui-muted uppercase">SKU / Barcode</Label>
                  <Label className="text-[10px] text-ui-muted uppercase text-center">Isi (Pcs)</Label>
                  <Label className="text-[10px] text-ui-muted uppercase text-right">Harga Jual</Label>
                  <Label className="text-[10px] text-ui-muted uppercase text-right">Modal</Label>
                  <span />
                  {form.units.map((u, i) => (
                    <Fragment key={i}>
                      <Input data-testid={`unit-name-${i}`} value={u.name} onChange={(e) => setUnit(i, "name", e.target.value)} placeholder="Dus" className="bg-ui-card border-ui-line h-10" />
                      <Input data-testid={`unit-barcode-${i}`} aria-label={`SKU / Barcode satuan ${u.name || i + 1}`} value={u.barcode || ""} onChange={(e) => setUnit(i, "barcode", e.target.value)} placeholder="SKU satuan (opsional)" className="bg-ui-card border-ui-line h-10 font-mono" />
                      <Input data-testid={`unit-pieces-${i}`} type="number" value={u.pieces} onChange={(e) => setUnit(i, "pieces", e.target.value)} className="bg-ui-card border-ui-line h-10 font-mono text-center" />
                      <MoneyInput data-testid={`unit-price-${i}`} value={u.price} onValueChange={(v) => setUnit(i, "price", v)} className="bg-ui-card border-ui-line h-10 font-mono text-right" />
                      <div data-testid={`unit-modal-${i}`} title="Harga beli/modal = harga beli Pcs × isi" className="h-10 flex items-center justify-end px-2 rounded-lg bg-ui-soft/70 border border-ui-line font-mono text-danger-readable text-sm">{rupiah(Number(form.cost || 0) * Number(u.pieces || 1))}</div>
                      <button type="button" data-testid={`unit-remove-${i}`} onClick={() => removeUnit(i)} className="h-10 w-9 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center">
                        <Trash2 className="h-4 w-4 text-danger-readable" />
                      </button>
                    </Fragment>
                  ))}
                </div>
                </div>
              )}
              {!editing && <p className="text-[11px] text-ui-muted">Isi SKU masing-masing satuan. Baris boleh dikosongkan jika produk tidak memakai satuan tambahan.</p>}
            </div>
            <ProductTierEditor tiers={form.price_tiers} units={form.units} cost={form.cost} baseUnit={editing?.base_unit || 'Pcs'} canViewCost={user?.role === 'admin'} onAdd={addTier} onChange={setTier} onRemove={removeTier}/>
            <div className="rounded-xl border border-ui-line p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wide text-ui-muted font-semibold">Varian (mis. Warna/Ukuran/Rasa)</div>
                <WriteButton type="button" data-testid="add-variant-btn" onClick={addVariant} size="sm" variant="outline" className="h-8 border-ui-line bg-transparent hover:bg-ui-card gap-1 text-xs">
                  <Plus className="h-3 w-3" /> Varian
                </WriteButton>
              </div>
              {form.variants.length === 0 ? (
                <p className="text-[11px] text-ui-muted">Tambahkan varian seperti Merah, Pink, Biru. Stok tetap digabung (varian hanya label). Di kasir muncul pilihan varian saat produk dipilih.</p>
              ) : (
                <div className="grid grid-cols-[1fr_1fr_36px] gap-2 items-center">
                  <Label className="text-[10px] text-ui-muted uppercase">Nama Varian</Label>
                  <Label className="text-[10px] text-ui-muted uppercase">SKU / Barcode (opsional)</Label>
                  <span />
                  {form.variants.map((v, i) => (
                    <Fragment key={i}>
                      <Input data-testid={`variant-name-${i}`} value={v.name} onChange={(e) => setVariant(i, "name", e.target.value)} placeholder="Merah" className="bg-ui-card border-ui-line h-10" />
                      <Input data-testid={`variant-barcode-${i}`} aria-label={`SKU / Barcode varian ${v.name || i + 1}`} value={v.barcode} onChange={(e) => setVariant(i, "barcode", e.target.value)} placeholder="SKU varian (opsional)" className="bg-ui-card border-ui-line h-10 font-mono" />
                      <button type="button" data-testid={`variant-remove-${i}`} onClick={() => removeVariant(i)} className="h-10 w-9 rounded-lg bg-ui-card hover:bg-danger-soft flex items-center justify-center">
                        <Trash2 className="h-4 w-4 text-danger-readable" />
                      </button>
                    </Fragment>
                  ))}
                </div>
              )}
              {!editing && <p className="text-[11px] text-ui-muted">Isi nama dan SKU setiap varian, atau kosongkan baris jika produk tidak memiliki varian.</p>}
            </div>
            <Field label="Harga Beli / Modal (per Pcs)">
              <MoneyInput data-testid="form-cost" value={form.cost} onValueChange={(v) => setForm({ ...form, cost: v })} className="bg-ui-card border-ui-line font-mono text-right" />
              <p className="text-[11px] text-ui-muted">Otomatis diperbarui dari harga Pembelian terakhir. Isi manual untuk modal awal.</p>
            </Field>
            {editing && <Field label="Alasan Perubahan Harga (opsional)"><Input data-testid="price-change-reason" value={priceReason} maxLength={500} onChange={(event) => setPriceReason(event.target.value)} placeholder="Contoh: harga supplier naik" className="bg-ui-card border-ui-line" /></Field>}
            <div className="grid grid-cols-2 gap-3">
              <Field label={editing ? "Stok (ubah via Opname/Pembelian/Transfer)" : "Stok Awal"}>
                <Input data-testid="form-stock" type="number" value={form.stock} disabled={!!editing} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="bg-ui-card border-ui-line font-mono text-right disabled:opacity-50 disabled:cursor-not-allowed" />
              </Field>
              <Field label="Stok Min">
                <Input data-testid="form-minstock" type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="bg-ui-card border-ui-line font-mono text-right" />
              </Field>
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Catatan Produk (mis. keterangan, promo, letak rak)</Label>
              <textarea
                data-testid="form-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Tulis catatan untuk produk ini…"
                rows={2}
                className="w-full rounded-lg bg-ui-card border border-ui-line text-sm p-2.5 text-ui-text placeholder:text-ui-muted focus:outline-none focus:ring-1 focus:ring-brand/50 resize-none"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ui-muted text-xs">Gambar Produk (foto / JPG)</Label>
              <div className="flex items-center gap-3">
                <div className="h-20 w-20 rounded-lg bg-ui-soft border border-ui-line overflow-hidden flex items-center justify-center shrink-0">
                  {form.image ? (
                    <ApiImage src={imgUrl(form.image)} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-7 w-7 text-ui-muted" />
                  )}
                </div>
                <div className="flex min-w-0 flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    <label data-testid="capture-image-label" className={`inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-brand-soft border border-brand/40 hover:border-brand text-brand-readable cursor-pointer text-sm font-semibold no-select ${uploading ? "pointer-events-none opacity-60" : ""}`}>
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                      {uploading ? "Mengunggah…" : "Ambil Foto"}
                      <input data-testid="form-image-camera" aria-label="Ambil foto produk dengan kamera belakang" type="file" accept="image/*" capture="environment" onChange={handleUpload} className="hidden" disabled={uploading} />
                    </label>
                    <label data-testid="upload-image-label" className={`inline-flex items-center gap-2 h-10 px-3 rounded-lg bg-ui-card border border-ui-line hover:border-brand cursor-pointer text-sm no-select ${uploading ? "pointer-events-none opacity-60" : ""}`}>
                      <Upload className="h-4 w-4" />
                      Pilih Galeri
                      <input data-testid="form-image-file" aria-label="Pilih foto produk dari galeri" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" disabled={uploading} />
                    </label>
                  </div>
                  <p className="text-[10px] text-ui-muted">Kamera memakai kamera belakang HP. Foto otomatis diperkecil maksimal 1200 px dan dikompres ke WEBP.</p>
                  {form.image && (
                    <button type="button" data-testid="remove-image-btn" onClick={() => setForm({ ...form, image: "" })} className="text-xs text-danger-readable hover:text-danger-readable text-left">Hapus gambar</button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={saving || packagesBusy} onClick={() => setOpen(false)} className="border-ui-line bg-transparent hover:bg-ui-card">Batal</Button>
            {editing && priceSettings?.store_id === priceSettings?.central_source_store_id && (
              <div className="flex flex-col items-end gap-1">
                <p data-testid="save-all-branches-note" className="max-w-xs text-right text-[10px] leading-tight text-warning-readable">Harga jual, tingkat harga, satuan, dan harga modal ikut berubah di semua cabang.</p>
                <WriteButton data-testid="save-product-all-branches-btn" onClick={() => save(true)} disabled={saving} variant="outline" className="border-warning text-warning-readable bg-warning-soft font-bold">
                  {savingAllBranches ? "Menerapkan…" : "Simpan ke Semua Cabang"}
                </WriteButton>
              </div>
            )}
            <WriteButton data-testid="save-product-btn" onClick={() => save(false)} disabled={saving} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold">{saving && !savingAllBranches ? "Menyimpan…" : "Simpan"}</WriteButton>
          </DialogFooter>
          </TabsContent>
          {!warehouseCatalog && <TabsContent value="packages" forceMount hidden={productTab !== 'packages'} className="min-w-0">
            {editing ? <ReceivingPackagesEditor key={editing.id} product={editing} onSaved={packagesSaved} onDirtyChange={setPackagesDirty} onBusyChange={setPackagesBusy} onClose={() => setOpen(false)} /> : <div className="rounded-lg border border-ui-line p-4 space-y-3"><p className="text-sm text-ui-muted">Simpan Data Produk terlebih dahulu, lalu buka Edit Produk → Kemasan Penerimaan. Kemasan tetap terhubung ke produk yang sama.</p><Button type="button" variant="outline" onClick={() => setProductTab('data')}>Kembali ke Data Produk</Button></div>}
          </TabsContent>}
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-ui-muted text-xs">{label}</Label>
      {children}
    </div>
  );
}
